import { EventDispatcher } from 'three';
import gsap from 'gsap';
import content from './getCurrentLangPopupContent';

const {
    basic,
    renaissanceSet,
    userGeneratedSet,
    screenLifeSet,
    typographySet,
    craftSet
} = content;

console.log(basic);

export const PopupConstructorEvent = {
    CLOSE: 'close',
    CLOSED: 'closed'
};

const linkLabel = basic.labels.workLink;
const closeLabel = basic.labels.close;

// Make a links map for async variant
const dataMap = {
    ...renaissanceSet.works,
    ...userGeneratedSet.works,
    ...screenLifeSet.works,
    ...typographySet.works,
    ...craftSet.works
};

// No tagName validation because of encapsulation
// Validate by yourself in order not to make browser do that
const tag = (tagName, className = null, innerHTML = null) => {
    tagName = tagName.toUpperCase();
    const el = document.createElement(tagName);

    if (innerHTML) el.innerHTML = innerHTML;

    if (className) el.classList.add(className);

    return el;
};

const appendLink = (data, targetEl) => {
    const {
        author, title, link, ariaLabel
    } = data;
    let linkEl = null;

    if (link) {
        const linkPEl = tag('p', 'popup__caption__link-wrap');

        linkEl = tag('a', null, linkLabel);

        linkEl.setAttribute('href', link);
        linkEl.setAttribute('target', '_blank');
        linkEl.setAttribute('rel', 'noopener');
        linkEl.setAttribute('aria-label', ariaLabel ? `${linkLabel}. ${ariaLabel}` : `${linkLabel}. ${title} ${author}`);

        linkPEl.appendChild(linkEl);
        targetEl.appendChild(linkPEl);

    }

    return linkEl;
};

const appendDescriptionItem = (data, targetEl, partClass) => {
    const { text } = data;

    if (!text) return { textEl: null, numParts: 0 };

    const textEl = tag('p', 'popup__description__item', text);

    const anyPart = [...textEl.querySelectorAll(`.${partClass}`)];

    if (anyPart.length < 1) {
        textEl.classList.add(partClass);
    }

    targetEl.appendChild(textEl);

    return {
        textEl,
        numParts: anyPart.length || 1
    };
};

const getShowHideTimeline = (el, duration, show, hide) => {
    const obj = {
        progress: 0
    };

    const tl = gsap.timeline({
        onStartParams: [el],
        onUpdateParams: [el, obj],
        onCompleteParams: [el],
        onStart: hide,
        onUpdate: (tgt, refObj) => {
            const p = refObj.progress;

            if (p > 0.01 && p < 0.99) show(tgt);
            else hide(tgt);
        },
        onComplete: hide
    });

    tl.addLabel('start');
    tl.to(obj, {
        duration,
        ease: 'linear',
        progress: 1
    });
    tl.addLabel('end');

    return tl;
};

const getHighlight = (data, duration, object) => {
    const { point, size } = data;
    const { x, y } = point;
    const { width, height } = object;
    const cx = x * width;
    const cy = y * height;
    const r = size * width * 0.5;

    const svg = `<svg viewBox="0 0 ${width} ${height}">
      <path d="M ${cx - r},${cy}
      a ${r},${r} 0 1,0 ${r * 2},0
      a ${r},${r} 0 1,0 ${-r * 2},0 M0,0L${width},0L${width},${height}L0,${height}z" />
    </svg>`;
    const el = tag('div', 'popup__display__highlight', svg);

    el.style.transformOrigin = `${x * 100}% ${y * 100}%`;

    const show = (tgt) => {
        tgt.classList.add('active');
    };
    const hide = (tgt) => {
        tgt.classList.remove('active');
    };

    const tl = getShowHideTimeline(el, duration, show, hide);

    return {
        el,
        tl
    };
};

const createCaption = (data, stgs) => {
    const {
        author, title, date, link, description, image
    } = data;
    const {
        textDelay,
        repeatDelay,
        partClass,
        textDurationDelta,
        textActiveClass
    } = stgs;

    const captionEl = tag('div', 'popup__caption');

    const titleWrapEl = tag('p', 'popup__caption__title-wrap');
    const titleEl = tag('span', 'popup__caption__title', title);
    const authorEl = tag('span', 'popup__caption__author', author);
    const dateEl = tag('span', 'popup__caption__date', date);
    let linkEl = null;
    let highlightEls = null;
    let descriptionTl = null;
    let descriptionDuration = 0;

    if (date) {
        if (author) titleWrapEl.appendChild(authorEl);
        titleWrapEl.appendChild(titleEl);
        titleWrapEl.appendChild(dateEl);
    } else {
        titleEl.innerHTML = `${title}. `;
        titleWrapEl.appendChild(titleEl);
        if (author) titleWrapEl.appendChild(authorEl);
    }

    captionEl.appendChild(titleWrapEl);

    if (link) linkEl = appendLink(data, captionEl);

    if (description instanceof Array) {
        const descrnEl = tag('div', 'popup__description');

        descriptionTl = gsap.timeline({
            delay: 0.25,
            paused: true,
            repeat: -1,
            repeatDelay
        });

        description.forEach((item) => {
            const { label, position, highlight } = item;
            const { textEl, numParts } = appendDescriptionItem(item, descrnEl, partClass);
            const duration = 1.2 * (item.duration || 1) + textDurationDelta * numParts;
            const tls = [];

            if (textEl) {
                const show = (tgt) => {
                    tgt.classList.add(textActiveClass);
                };
                const hide = (tgt) => {
                    tgt.classList.remove(textActiveClass);
                };
                const tl = getShowHideTimeline(textEl, duration, show, hide);

                tls.push(tl);

                if (textEl) textEl.dataset.dutation = duration;
            }

            if (highlight) {
                const { el, tl } = getHighlight(highlight, duration, image);

                tls.push(tl);
                highlightEls = highlightEls || [];
                highlightEls.push(el);
            }

            tls.forEach((tl, index) => {
                if (position) {
                    descriptionTl.add(tl, position);
                } else {
                    if (label) {
                        if (index < 1) descriptionTl.addLabel(label, descriptionDuration);
                        descriptionTl.add(tl, label);
                    } else {
                        descriptionTl.add(tl, descriptionDuration);
                    }

                    descriptionDuration += tl.duration();
                    descriptionDuration += textDelay;
                }
            });

        });

        captionEl.appendChild(descrnEl);
    }

    return {
        captionEl, linkEl, descriptionTl, highlightEls
    };
};

const createFigure = (data, stgs) => {
    const figureEl = tag('figure', 'popup__figure');
    const displayContEl = tag('div', 'popup__display');
    const displayWrapEl = tag('div', 'popup__display__wrap');
    const displayAppearWrapEl = tag('span', 'popup__display__appear-wrap');
    const {
        captionEl, linkEl, descriptionTl, highlightEls
    } = createCaption(data, stgs);

    displayWrapEl.appendChild(displayAppearWrapEl);
    displayContEl.appendChild(displayWrapEl);
    figureEl.appendChild(displayContEl);
    figureEl.appendChild(captionEl);

    return {
        figureEl, displayContEl, displayWrapEl, displayAppendWrapEl: displayAppearWrapEl, linkEl, descriptionTl, highlightEls
    };
};

const createCloseButton = () => {
    const svg = `<svg viewBox="-14 -14 28 28">
      <line x1="-13.75" y1="-13.75" x2="13.75" y2="13.75" stroke="black" stroke-width="1" />
      <line x1="-13.75" y1="13.75" x2="13.75" y2="-13.75" stroke="black" stroke-width="1" />
    </svg>`;
    const el = tag('button', 'popup__close', svg);

    el.setAttribute('aria-label', closeLabel);

    return el;
};

const destroy = (el) => {
    if (el.parentNode) el.parentNode.removeChild(el);
};

const createImage = (data) => {
    if (!data) return null;

    const {
        src, srcset, sizes, alt, width, height
    } = data;
    const el = tag('img', 'popup__img');

    el.setAttribute('alt', alt);
    el.setAttribute('src', src);
    el.setAttribute('srcset', srcset);
    el.setAttribute('sizes', sizes);
    el.setAttribute('width', width);
    el.setAttribute('height', height);

    return el;
};

const createVideo = (data) => {
    if (!data) return null;
    const {
        sources, poster, width, height
    } = data;
    const el = tag('video', 'popup__video');

    el.setAttribute('poster', poster);
    el.setAttribute('width', width);
    el.setAttribute('height', height);
    el.setAttribute('loop', 'loop');
    el.setAttribute('autoplay', 'autoplay');
    el.setAttribute('playsinline', 'playsinline');
    el.setAttribute('muted', 'muted');
    el.muted = 'muted';

    sources.forEach((source) => {
        const { src, type } = source;
        const srcEl = tag('source');

        srcEl.setAttribute('src', src);
        srcEl.setAttribute('type', type);

        el.appendChild(srcEl);
    });

    return el;
};

const createPopupBlock = (data, stgs) => {
    const { type, image, video } = data;

    const popupEl = tag('div', 'popup');
    const popupWrap = tag('div', 'popup__wrap');
    const {
        figureEl, displayContEl, displayWrapEl, displayAppendWrapEl, linkEl, descriptionTl, highlightEls
    } = createFigure(data, stgs);
    const closeBtn = createCloseButton();
    const sizeConf = type === 'video' ? video : image;
    const displayEl = type === 'video' ? createVideo(video) : createImage(image);

    if (sizeConf) {
        const { width, height } = sizeConf;

        displayContEl.style.setProperty('--pic-aspect-ratio', (width / height).toString());
        displayWrapEl.style.setProperty('--pic-aspect-ratio', (width / height).toString());
    }

    if (displayEl) displayAppendWrapEl.appendChild(displayEl);
    if (highlightEls instanceof Array) {
        highlightEls.forEach((el) => {
            displayAppendWrapEl.appendChild(el);
        });
    }
    popupWrap.appendChild(figureEl);
    popupWrap.appendChild(closeBtn);
    popupEl.appendChild(popupWrap);

    return {
        popupEl, displayWrapEl, displayContEl, displayEl, linkEl, descriptionTl, closeBtn
    };
};

// Make async, if there would be a lot of information
const getData = (id) => dataMap[id] || null;

const defaults = {
    textActiveClass: 'active',
    partClass: 'part',
    textDurationDelta: 0.6,
    repeatDelay: 2,
    textDelay: 0.4 // change to 0
};

class PopupConstructor extends EventDispatcher {
    constructor() {
        super();

        this._isActive = false;
        this._activeId = null;
    }

    show(id) {
        if (this._activeId === id) return true;

        const data = getData(id);

        if (!data) return false;

        if (this._isActive) {
            // Change content
            // Is unnecessary at the moment because the triggers are unavailable
        } else {
            this._addPopup(data);
            this._addEventListeners();

            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    this._popupEl.classList.add('active');
                    document.body.classList.add('popup-open');

                    if (this._descriptionTl) this._descriptionTl.play();
                });
            });

        }

        this._activeId = id;
        this._isActive = true;

        return true;
    }

    hide() {
        if (!this._isActive) return;

        this._killDescription();
        this._removeEventListeners();

        if (this._popupEl) {
            const popupEl = this._popupEl;

            popupEl.classList.remove('active');

            setTimeout(() => {
                document.body.classList.remove('popup-open');
                this._hidden();
            }, 500);
            setTimeout(() => {
                destroy(popupEl);
            }, 1000);

            this._closeBtn = null;
            this._displayEl = null;
            this._displayWrapEl = null;
            this._displayContEl = null;
            this._linkEl = null;
            this._popupEl = null;
        }

        this._prevActiveId = this._activeId;
        this._activeId = null;
        this._isActive = false;
    }

    _addPopup(data) {
        const {
            popupEl, displayWrapEl, displayContEl, displayEl, linkEl, descriptionTl, closeBtn
        } = createPopupBlock(data, defaults);

        document.body.appendChild(popupEl);

        this._closeBtn = closeBtn;
        this._displayEl = displayEl;
        this._displayWrapEl = displayWrapEl;
        this._displayContEl = displayContEl;
        this._linkEl = linkEl;
        this._popupEl = popupEl;
        this._descriptionTl = descriptionTl;
    }

    _addEventListeners() {
        if (!this._popupEl) return;

        if (this._displayWrapEl) {
            this._displayResizeHandler = () => {
                const h = this._displayContEl.getBoundingClientRect().height;
                const w = this._displayWrapEl.getBoundingClientRect().width;

                this._displayContEl.style.setProperty('--pic-width', `${w}px`);
                this._displayContEl.style.setProperty('--pic-height', `${h}px`);
            };

            this._escHandler = (evt) => {
                const isTab = evt.keyCode === 9;

                // Esc || Tab
                if (evt.keyCode === 27 || isTab) this._handleClose('Keydown', isTab);
            };

            requestAnimationFrame(() => {
                setTimeout(() => {
                    this._displayResizeHandler();
                }, 100);
            });

            window.addEventListener('resize', this._displayResizeHandler);
            window.addEventListener('keydown', this._escHandler);
        }

        if (this._linkEl) {
            this._linksHandler = (evt) => {
                this._bubbleEvent(evt);
            };
            this._linkEl.addEventListener('click', this._linksHandler);
        }

        this._closeHandler = () => {
            this._handleClose('Click');
        };
        this._closeBtn.addEventListener('click', this._closeHandler);
    }

    _removeEventListeners() {
        if (!this._popupEl) return;

        if (this._linkEl && this._linksHandler) this._linkEl.removeEventListener('click', this._linksHandler);
        if (this._displayResizeHandler) window.removeEventListener('resize', this._displayResizeHandler);
        if (this._escHandler) window.removeEventListener('keydown', this._escHandler);
        if (this._closeHandler) this._closeBtn.removeEventListener('click', this._closeHandler);

        this._displayResizeHandler = null;
        this._escHandler = null;
        this._linksHandler = null;
        this._closeHandler = null;
    }

    _killDescription() {
        if (!this._descriptionTl) return;

        this._descriptionTl.kill();
        this._descriptionTl = null;
    }

    _hidden() {
        this.dispatchEvent({
            type: PopupConstructorEvent.CLOSED,
            workId: this._prevActiveId
        });
    }

    _handleClose(action, isTab) {
        this.dispatchEvent({
            type: PopupConstructorEvent.CLOSE,
            workId: this._activeId,
            action,
            forceNavUpdate: isTab
        });
    }

    _bubbleEvent(evt) {
        this.dispatchEvent({
            type: evt.type,
            originalEvent: evt
        });
    }

    get isActive() {
        return this._isActive;
    }

    get activeId() {
        return this._activeId;
    }
}

export default PopupConstructor;
